import { Select, Row, Col, Tag } from 'antd-v4'
import React, { Component } from 'react'
import { CreateProduct, CreateOverridePrice, CreateChildProduct } from '../../ProductStore'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { inject, observer } from 'mobx-react'
import { ICreateMultiLocationPrice } from '@getgreenline/homi-shared'
import { Icon, Divider, Tooltip } from 'antd'
import { BlazeButton } from '../../../../../../components/CustomButtons/BlazeButton'
import { CustomDivider } from '../../../../../../components/CustomDivider'
import { CurrencyInput } from '../../../../../../components/CurrencyInput'
import { PercentageInput } from '../../../../../../components/Input/PercentageInput'
import { BlazeSelectFilter } from '../../../../../../components/CustomSelect/BlazeSelectFilter'

interface Props {
  product: CreateProduct | CreateChildProduct
  currentCompanyStore?: CurrentCompanyStore
}

@inject('currentCompanyStore')
@observer
export class MultiLocationPrices extends Component<Props> {
  constructor(props: Props) {
    super(props)
  }

  locations = this.props.currentCompanyStore!.locations || []

  company = this.props.currentCompanyStore!.company!

  addLocationOverride = () => {
    const { product } = this.props
    const availableLocations = this.getLocationOptions()

    if (availableLocations.length > 0) {
      const overridePrice: ICreateMultiLocationPrice = {
        locationId: availableLocations[0].id,
        productId: product.id || null,
        retailPrice: null,
        wholesaleCost: null,
      }

      const newOverride = new CreateOverridePrice(overridePrice, product.purchasePrice)

      product.overridePrices.push(newOverride)
    }
    product.changed = true
  }

  getLocationOptions = (locationId?: number | null) => {
    const { overrideLocationIds } = this.props.product

    return this.locations.filter(
      (location) => !overrideLocationIds.includes(location.id) || location.id === locationId,
    )
  }

  get filteredOverridePrices() {
    const { overridePrices } = this.props.product

    // Only show override prices for locations that user has permissions to
    return overridePrices.filter((price) =>
      this.locations.find((loc) => price.locationId === loc.id),
    )
  }

  get isButtonDisabled() {
    if (this.props.product.isGiftCard) {
      return true
    }
    return this.filteredOverridePrices.length >= this.locations.length
  }

  render() {
    const { product } = this.props

    return (
      <div>
        <CustomDivider title='Multi-location price' className='mt-0' />

        <BlazeButton
          titleProps={{
            title: this.isButtonDisabled ? 'All locations selected' : 'Add multi-location price',
          }}
          buttonProps={{
            icon: 'plus',
            disabled: this.isButtonDisabled,
            onClick: this.addLocationOverride,
          }}
        />

        {this.filteredOverridePrices.map((overridePrice, index) => {
          return (
            <div key={index}>
              {index !== 0 && <Divider className='mb-0 mt-3' />}

              <Row className='d-flex flex-wrap align-items-end'>
                <Col className='mr-2 mt-3'>
                  <label className='mb-2'>Location</label>
                  <div>
                    <BlazeSelectFilter<number>
                      maxTagTextLength={1}
                      dropdownMatchSelectWidth={false}
                      value={overridePrice.locationId}
                      disabled={overridePrice.retailPrice !== null}
                      onChange={(locationId) => {
                        overridePrice.setLocationId(locationId)
                        product.changed = true
                      }}
                    >
                      {this.getLocationOptions(overridePrice.locationId).map((loc) => {
                        return (
                          <Select.Option key={loc.id} value={loc.id}>
                            {loc.name}
                          </Select.Option>
                        )
                      })}
                    </BlazeSelectFilter>
                  </div>
                </Col>

                <Col className='mr-2 mt-3'>
                  <label className='mb-2'>
                    Retail price
                    <Tooltip
                      getTooltipContainer={() => document.body}
                      title='The price your product will be sold at'
                    >
                      <Icon className='ml-2' type='question-circle' />
                    </Tooltip>
                  </label>

                  <div>
                    <CurrencyInput
                      className='gr-currency-input'
                      centValue={overridePrice.retailPrice}
                      onChange={(newRetailPrice) => {
                        overridePrice.setRetailPrice(newRetailPrice)
                        overridePrice.setMargin(product.purchasePrice)
                        overridePrice.setMarkup(product.purchasePrice)
                        product.changed = true
                      }}
                    />
                  </div>
                </Col>

                {/* Wholesale cost override is being assessed. Commented until further notice */}
                {/* {[594].includes(this.company.id) && (
                  <Col className='mr-2 mt-3'>
                    <label className='mb-2'>
                      Wholesale cost
                      <Tooltip
                        getTooltipContainer={() => document.body}
                        title={
                          <>
                            <div>Estimated cost of purchasing 1 unit/gram.</div>
                            <div>This cost is set by your latest purchase order</div>
                          </>
                        }
                      >
                        <Icon className='ml-2' type='question-circle' />
                      </Tooltip>
                    </label>

                    <div>
                      <CurrencyInput
                        className='gr-currency-input'
                        style={{ width: '100%' }}
                        centValue={overridePrice.wholesaleCost}
                        onChange={(newWholesaleCost) =>
                          overridePrice.setWholesaleCost(newWholesaleCost)
                        }
                      />
                    </div>
                  </Col>
                )} */}

                <Col className='mr-2 mt-3'>
                  <label className='mb-2'>Margin</label>

                  {overridePrice.margin < 0 && (
                    <Tag className='ml-2' color='red'>
                      Negative
                    </Tag>
                  )}

                  <div className='d-flex'>
                    <PercentageInput
                      value={overridePrice.margin}
                      style={{ flexGrow: 1 }}
                      onChange={(margin) => {
                        overridePrice.setMargin(product.purchasePrice, Number(margin))
                        product.changed = true
                      }}
                    />
                  </div>
                </Col>

                <Col className='mr-2 mt-3'>
                  <label className='mb-2'>Markup</label>

                  {overridePrice.markup < 0 && (
                    <Tag className='ml-2' color='red'>
                      Negative
                    </Tag>
                  )}

                  <div className='d-flex'>
                    <PercentageInput
                      value={overridePrice.markup}
                      style={{ flexGrow: 1 }}
                      onChange={(markup) => {
                        overridePrice.setMarkup(product.purchasePrice, Number(markup))
                        product.changed = true
                      }}
                    />
                  </div>
                </Col>

                <Col className='mr-2 mt-3'>
                  <BlazeButton
                    titleProps={{
                      title: 'Delete',
                    }}
                    buttonProps={{
                      icon: 'delete',
                      type: 'danger',
                      ghost: true,
                      onClick: () => product.removePriceOverride(overridePrice),
                    }}
                  />
                </Col>
              </Row>
            </div>
          )
        })}
      </div>
    )
  }
}
